"use client";

import { useEffect, useRef, useState } from "react";

import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export function SearchableCombobox({
  items,
  selectedValue,
  onSelect,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  renderItem,
  renderSelected,
  label,
  ariaLabel,
  className,
  popoverContentClassName,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Index of the keyboard-highlighted (active) result. Defaults to the first
  // result so a query is immediately confirmable with Enter.
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const activeItemRef = useRef(null);
  const listboxId = useRef(`combobox-listbox-${Math.random().toString(36).slice(2, 9)}`).current;

  const selectedItem = items.find((item) => item.id === selectedValue);

  const filtered = search
    ? items.filter((item) =>
        (item.name || "").toLowerCase().includes(search.toLowerCase())
      )
    : items;

  // Re-highlight the first result whenever the query changes, so the top match
  // is always the one Enter selects.
  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const selectItem = (item) => {
    if (!item) return;
    onSelect(item.id);
    setOpen(false);
    setSearch("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectItem(filtered[activeIndex]);
    }
  };

  const activeItem = filtered[activeIndex];

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="text-sm font-medium leading-none">
          {label}
        </label>
      )}
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (nextOpen) {
            // Open with the currently-selected item highlighted (or the first).
            const selectedIdx = items.findIndex(
              (item) => item.id === selectedValue
            );
            setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
          } else {
            setSearch("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls={listboxId}
            disabled={disabled}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 ring-offset-background",
              "hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
          >
            <span className="truncate">
              {selectedItem ? (
                renderSelected ? (
                  renderSelected(selectedItem)
                ) : (
                  selectedItem.name
                )
              ) : (
                <span className="text-gray-500">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-[var(--radix-popover-trigger-width)] p-0",
            popoverContentClassName
          )}
          align="start"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-label={searchPlaceholder}
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                activeItem ? `${listboxId}-opt-${activeItem.id}` : undefined
              }
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div
            id={listboxId}
            role="listbox"
            // When this combobox is opened inside a modal Dialog, the popover is
            // portalled to <body>, i.e. outside the dialog's react-remove-scroll
            // subtree. react-remove-scroll listens for wheel/touch on `document`
            // (bubble phase) and preventDefaults them, so the trackpad can't scroll
            // this list (only the scrollbar works). Stopping propagation here keeps
            // the event from reaching that listener, restoring native scroll.
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="max-h-[300px] overflow-y-auto p-1"
          >
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            ) : (
              filtered.map((item, index) => {
                const isActive = index === activeIndex;
                const isSelected = selectedValue === item.id;
                return (
                  <button
                    key={item.id}
                    id={`${listboxId}-opt-${item.id}`}
                    ref={isActive ? activeItemRef : null}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => selectItem(item)}
                    onMouseMove={() => setActiveIndex(index)}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none items-center justify-start rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                      isActive && "bg-accent text-accent-foreground",
                      isSelected && !isActive && "bg-accent/50"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {renderItem ? (
                      renderItem(item)
                    ) : (
                      <span className="min-w-0 flex-1 text-left">
                        {item.name}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
